import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Ultraman Card Game themed loading flavor texts.
 * Shown randomly during loading states for personality.
 */
const FLAVOR_TEXTS = [
  "Consulting the Land of Light",
  "Awaiting orders from Zoffy",
  "When Tri-Squad???",
  "Watch out for Zetton",
  "Receiving transmission from M78",
  "Assembling the Ultra Brothers",
  "Negotiating with a very confused Kaiju",
  "Listening to the hum of the Plasma Spark",
  "Consulting the archives of the Science Patrol",
  "Shuffling the cards because rules are rules",
  "Testing if your deck can survive Zetton",
  "Wondering if that mulligan was a good idea",
  "Recalculating mulligan odds to protect your sanity",
  "Validating that your decklist isn't secretly 51 cards",
];

/**
 * Pick a random flavor text. Memoized per mount so it doesn't
 * flicker between re-renders while the spinner is visible.
 */
const useRandomFlavor = (customText) => {
  return useMemo(() => {
    if (customText) return customText;
    return FLAVOR_TEXTS[Math.floor(Math.random() * FLAVOR_TEXTS.length)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable per mount
};

/**
 * Shared spiral loading spinner used across the app.
 *
 * Sizes:
 *  - 'sm'  → 32px  (inline / sidebar)
 *  - 'md'  → 64px  (default, deck builder)
 *  - 'lg'  → 80px  (full-page gallery)
 *
 * @param {string}  [size='md']       Spinner size preset
 * @param {string}  [text]            Override flavor text (or omit for random)
 * @param {boolean} [showText=true]   Whether to show text at all
 * @param {string}  [className]       Extra wrapper classes
 */
export default function SpiralSpinner({ size = 'md', text, showText = true, className = '' }) {
  const flavor = useRandomFlavor(text);

  const sizeClasses = {
    sm: 'spiral-spinner-sm',
    md: '',
    lg: 'spiral-spinner-lg',
  };

  return (
    <div className={`session-loading-overlay ${className}`}>
      <div className={`spiral-spinner ${sizeClasses[size] || ''}`}>
        <div className="spiral-ring spiral-ring-1"></div>
        <div className="spiral-ring spiral-ring-2"></div>
        <div className="spiral-ring spiral-ring-3"></div>
      </div>
      {showText && <p className="loading-text">{flavor}…</p>}
    </div>
  );
}

SpiralSpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  text: PropTypes.string,
  showText: PropTypes.bool,
  className: PropTypes.string,
};

export { FLAVOR_TEXTS };
